import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, BadgeCheck, Clock3, Copy, FileText, Link2, RefreshCw, Share2, ShieldCheck, Star, UploadCloud } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { logClientError } from '@/utils/clientError';
import { formatBadgeLabel } from '@/utils/displayLabels';

const KYC_STATUS_STYLE = {
  not_submitted: 'bg-slate-100 text-slate-700',
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const KYC_STATUS_LABEL = {
  not_submitted: 'Non soumis',
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Rejeté',
};

const KYC_DOCUMENT_FIELDS = [
  { category: 'kyc_document', label: 'Document entreprise / RCCM', helper: 'RCCM, registre de commerce ou équivalent.' },
  { category: 'kyc_identity', label: "Pièce d'identité", helper: 'Carte nationale, passeport ou permis valide.' },
  { category: 'kyc_address_proof', label: "Preuve d'adresse", helper: 'Facture, attestation ou document récent.' },
];

const DEFAULT_RATING_DISTRIBUTION = { '5': 0, '4': 0, '3': 0, '2': 0, '1': 0 };

const createEmptyDocumentState = () => KYC_DOCUMENT_FIELDS.reduce((accumulator, item) => ({
  ...accumulator,
  [item.category]: {
    file_id: '',
    original_filename: '',
    access_url: '',
  },
}), {});

const createEmptyKycForm = () => ({
  business_name: '',
  business_activity: '',
  document_type: '',
  registration_number: '',
  tax_identifier: '',
  professional_address: '',
  business_city: '',
  business_country: '',
  business_contact_phone: '',
  business_contact_email: '',
  notes: '',
  documents: createEmptyDocumentState(),
});

const buildAbsoluteUrl = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';
  if (/^https?:\/\//i.test(rawValue)) return rawValue;
  const path = rawValue.startsWith('/') ? rawValue : `/${rawValue}`;
  return `${window.location.origin}${path}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const buildInitialFormFromKyc = (kyc) => {
  const form = createEmptyKycForm();
  if (!kyc) return form;
  const documents = createEmptyDocumentState();
  (kyc.documents || []).forEach((item) => {
    if (!item?.category || !documents[item.category]) return;
    documents[item.category] = {
      file_id: item.file_id || '',
      original_filename: item.original_filename || item.label || '',
      access_url: item.access_url || '',
    };
  });
  return {
    business_name: kyc.business_name || '',
    business_activity: kyc.business_activity || '',
    document_type: kyc.document_type || '',
    registration_number: kyc.registration_number || '',
    tax_identifier: kyc.tax_identifier || '',
    professional_address: kyc.professional_address || '',
    business_city: kyc.business_city || '',
    business_country: kyc.business_country || '',
    business_contact_phone: kyc.business_contact_phone || '',
    business_contact_email: kyc.business_contact_email || '',
    notes: kyc.seller_notes || kyc.notes || '',
    documents,
  };
};

const StatusBanner = ({ workflowState, rejectionReason, reviewNotes, isVerified }) => {
  const state = {
    not_submitted: {
      icon: ShieldCheck,
      className: 'border-slate-200 bg-slate-50 text-slate-700',
      title: 'Dossier KYC non soumis',
      description: 'Soumettez vos informations et vos trois pièces justificatives pour lancer la vérification.',
    },
    pending: {
      icon: Clock3,
      className: 'border-amber-200 bg-amber-50 text-amber-800',
      title: 'Dossier KYC en attente',
      description: 'Votre dossier a été soumis. L’administration le vérifie actuellement.',
    },
    approved: {
      icon: BadgeCheck,
      className: 'border-green-200 bg-green-50 text-green-800',
      title: 'Vendeur vérifié',
      description: isVerified
        ? 'Votre vérification KYC est approuvée. Votre badge vendeur vérifié est actif.'
        : 'Votre vérification KYC est approuvée.',
    },
    rejected: {
      icon: AlertTriangle,
      className: 'border-red-200 bg-red-50 text-red-800',
      title: 'Dossier KYC à corriger',
      description: rejectionReason || reviewNotes || 'Votre dossier a été rejeté. Corrigez les informations demandées puis resoumettez.',
    },
  }[workflowState] || null;

  const banner = state || {
    icon: ShieldCheck,
    className: 'border-slate-200 bg-slate-50 text-slate-700',
    title: 'Dossier KYC non soumis',
    description: 'Soumettez vos informations et vos trois pièces justificatives pour lancer la vérification.',
  };
  const Icon = banner.icon;

  return (
    <div className={`rounded-lg border p-4 ${banner.className}`}>
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <div className="space-y-1">
          <p className="font-semibold">{banner.title}</p>
          <p className="text-sm">{banner.description}</p>
        </div>
      </div>
    </div>
  );
};

const TrustCenterPanel = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCategory, setUploadingCategory] = useState('');
  const [user, setUser] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [badges, setBadges] = useState([]);
  const [reviewsSummary, setReviewsSummary] = useState({
    average_rating: 0,
    total_reviews: 0,
    rating_distribution: DEFAULT_RATING_DISTRIBUTION,
    primary_badge: null,
  });
  const [reviews, setReviews] = useState([]);
  const [kycForm, setKycForm] = useState(createEmptyKycForm());
  const [resubmissionMode, setResubmissionMode] = useState(false);
  const [reviewLinkUrl, setReviewLinkUrl] = useState('');

  const loadTrustData = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const meResponse = await axios.get(`${API}/auth/me`);
      const me = meResponse.data;
      setUser(me);
      setKyc(me.kyc || null);
      setBadges(me.seller_badges || []);
      setReviewsSummary({
        average_rating: me.seller_reputation?.average_rating || 0,
        total_reviews: me.seller_reputation?.total_reviews || 0,
        rating_distribution: me.seller_reputation?.rating_distribution || DEFAULT_RATING_DISTRIBUTION,
        primary_badge: me.seller_reputation?.primary_badge || null,
      });
      setReviewLinkUrl(buildAbsoluteUrl(me.seller_review_link_url || me.seller_review_link_path || ''));

      const currentOrgId = me.current_org_id;
      if (currentOrgId) {
        try {
          const reviewsResponse = await axios.get(`${API}/vendors/${currentOrgId}/reviews?page=1&page_size=10`);
          setReviews(reviewsResponse.data?.items || []);
          setReviewsSummary({
            average_rating: reviewsResponse.data?.average_rating || 0,
            total_reviews: reviewsResponse.data?.total_reviews || 0,
            rating_distribution: reviewsResponse.data?.rating_distribution || DEFAULT_RATING_DISTRIBUTION,
            primary_badge: me.seller_reputation?.primary_badge || null,
          });
          if (!me.seller_review_link_url && !me.seller_review_link_path) {
            setReviewLinkUrl(buildAbsoluteUrl(reviewsResponse.data?.review_link_url || reviewsResponse.data?.review_link_path || `/vendors/${currentOrgId}/review`));
          }
        } catch (error) {
          logClientError('trust_center.load_vendor_reviews', error);
          setReviews([]);
        }
      }
    } catch (error) {
      logClientError('trust_center.load_data', error);
      toast.error('Impossible de charger les informations de confiance');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTrustData();
  }, []);

  useEffect(() => {
    setKycForm(buildInitialFormFromKyc(kyc));
    setResubmissionMode(false);
  }, [kyc]);

  const workflowState = kyc?.workflow_state || user?.kyc_status || 'not_submitted';
  const isVerified = useMemo(
    () => workflowState === 'approved' || badges.some((badge) => badge.badge_type === 'verified_seller'),
    [badges, workflowState]
  );
  const shouldShowForm = workflowState === 'not_submitted' || (workflowState === 'rejected' && resubmissionMode);

  const handleKycInputChange = (field, value) => {
    setKycForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCopyReviewLink = async () => {
    if (!reviewLinkUrl) {
      toast.error('Lien de collecte indisponible pour le moment.');
      return;
    }
    try {
      await navigator.clipboard.writeText(reviewLinkUrl);
      toast.success('Lien d’avis copié');
    } catch (error) {
      logClientError('trust_center.copy_review_link', error);
      toast.error('Impossible de copier le lien d’avis');
    }
  };

  const handleShareReviewLink = async () => {
    if (!reviewLinkUrl) {
      toast.error('Lien de collecte indisponible pour le moment.');
      return;
    }
    if (!navigator.share) {
      await handleCopyReviewLink();
      return;
    }
    try {
      await navigator.share({
        title: 'Laisser un avis sur notre boutique Coli Noki',
        text: 'Merci de partager votre expérience de livraison.',
        url: reviewLinkUrl,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        logClientError('trust_center.share_review_link', error);
        toast.error('Impossible de partager le lien d’avis');
      }
    }
  };

  const handleDocumentUpload = async (category, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    setUploadingCategory(category);
    try {
      const response = await axios.post(`${API}/kyc/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedFile = response.data?.data?.file;
      if (!uploadedFile?.id) {
        throw new Error('missing_file_id');
      }
      setKycForm((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          [category]: {
            file_id: uploadedFile.id,
            original_filename: uploadedFile.original_filename || file.name,
            access_url: uploadedFile.access_url || '',
          },
        },
      }));
      toast.success(`${KYC_DOCUMENT_FIELDS.find((item) => item.category === category)?.label || 'Document'} téléversé`);
    } catch (error) {
      logClientError('trust_center.upload_kyc_document', error);
      toast.error(error.response?.data?.detail || 'Impossible de téléverser ce document');
      event.target.value = '';
    } finally {
      setUploadingCategory('');
    }
  };

  const validateStructuredKycForm = () => {
    if (!kycForm.business_name || !kycForm.document_type || !kycForm.professional_address || !kycForm.business_city || !kycForm.business_country || !kycForm.business_contact_phone) {
      toast.error('Renseignez les informations entreprise et contact obligatoires.');
      return false;
    }
    const missingDocument = KYC_DOCUMENT_FIELDS.find(({ category }) => !kycForm.documents[category]?.file_id);
    if (missingDocument) {
      toast.error(`Ajoutez le document obligatoire : ${missingDocument.label}.`);
      return false;
    }
    return true;
  };

  const handleSubmitKyc = async (event) => {
    event.preventDefault();
    if (!validateStructuredKycForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        business_name: kycForm.business_name,
        business_activity: kycForm.business_activity || undefined,
        document_type: kycForm.document_type,
        registration_number: kycForm.registration_number || undefined,
        tax_identifier: kycForm.tax_identifier || undefined,
        professional_address: kycForm.professional_address,
        business_city: kycForm.business_city,
        business_country: kycForm.business_country,
        business_contact_phone: kycForm.business_contact_phone,
        business_contact_email: kycForm.business_contact_email || undefined,
        notes: kycForm.notes || undefined,
        documents: KYC_DOCUMENT_FIELDS.map((item) => ({
          category: item.category,
          label: item.label,
          file_id: kycForm.documents[item.category]?.file_id,
          document_type: item.category === 'kyc_document' ? kycForm.document_type : item.label,
        })),
      };
      const response = await axios.post(`${API}/kyc/submit`, payload);
      if (response.data?.data?.kyc) {
        setKyc(response.data.data.kyc);
      }
      setResubmissionMode(false);
      toast.success(workflowState === 'rejected' ? 'Dossier KYC resoumis avec succès.' : 'Dossier KYC soumis avec succès.');
      await loadTrustData({ silent: true });
    } catch (error) {
      logClientError('trust_center.submit_kyc', error);
      toast.error(error.response?.data?.detail || 'Impossible de soumettre le dossier KYC');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-gray-500">Chargement du centre de confiance...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-cyan-600" />
                Vérification KYC vendeur
              </CardTitle>
              <CardDescription>
                Soumettez un dossier complet pour activer le badge vendeur vérifié et renforcer la confiance de vos clients.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={KYC_STATUS_STYLE[workflowState] || 'bg-slate-100 text-slate-700'}>{KYC_STATUS_LABEL[workflowState] || workflowState}</Badge>
              <Button variant="outline" size="sm" onClick={() => loadTrustData({ silent: true })} disabled={refreshing}>
                <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <StatusBanner workflowState={workflowState} rejectionReason={kyc?.rejection_reason} reviewNotes={kyc?.review_notes} isVerified={isVerified} />

          {kyc && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Dossier vendeur</h3>
                  <p className="text-sm text-slate-500">
                    Version {kyc.version || 1}
                    {kyc.submitted_at ? ` • soumis le ${formatDateTime(kyc.submitted_at)}` : ''}
                  </p>
                </div>
                {isVerified && <Badge className="bg-green-100 text-green-700">Vendeur vérifié</Badge>}
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Entreprise / boutique</p>
                  <p className="font-medium text-slate-900">{kyc.business_name || '-'}</p>
                  <p className="text-sm text-slate-600">{kyc.business_activity || 'Activité non renseignée'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Coordonnées</p>
                  <p className="font-medium text-slate-900">{kyc.business_contact_phone || '-'}</p>
                  <p className="text-sm text-slate-600">{kyc.business_contact_email || 'Email non renseigné'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Immatriculation</p>
                  <p className="font-medium text-slate-900">{kyc.document_type || '-'}</p>
                  <p className="text-sm text-slate-600">RCCM : {kyc.registration_number || 'Non renseigné'}</p>
                  <p className="text-sm text-slate-600">NIU / fiscal : {kyc.tax_identifier || 'Non renseigné'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Adresse professionnelle</p>
                  <p className="font-medium text-slate-900">{kyc.professional_address || '-'}</p>
                  <p className="text-sm text-slate-600">{[kyc.business_city, kyc.business_country].filter(Boolean).join(', ') || 'Ville / pays non renseignés'}</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <p className="text-xs uppercase tracking-wide text-slate-500">Pièces justificatives</p>
                <div className="grid gap-2 md:grid-cols-3">
                  {(kyc.documents || []).map((documentItem) => (
                    <div key={documentItem.category} className="rounded-md border border-slate-200 p-3 text-sm">
                      <p className="font-medium text-slate-900">{documentItem.label || documentItem.category}</p>
                      <p className="text-slate-500">{documentItem.original_filename || 'Document sécurisé'}</p>
                      {documentItem.access_url ? (
                        <a href={documentItem.access_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-cyan-700 underline">
                          <FileText className="h-4 w-4" />
                          Ouvrir
                        </a>
                      ) : (
                        <span className="mt-2 inline-block text-xs text-slate-500">Accès temporaire indisponible</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {Array.isArray(kyc.document_checklist) && kyc.document_checklist.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Complétude du dossier</p>
                  <div className="grid gap-2 md:grid-cols-3">
                    {kyc.document_checklist.map((item) => (
                      <div
                        key={`checklist-${item.category}`}
                        className={`rounded-md border p-3 text-sm ${
                          item.is_present ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'
                        }`}
                      >
                        <p className="font-medium">{item.label}</p>
                        <p className="mt-1 text-xs">
                          {item.is_present ? 'Document présent' : 'Document manquant'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {kyc.seller_notes && <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-700"><strong>Notes vendeur :</strong> {kyc.seller_notes}</div>}
              {kyc.review_notes && workflowState !== 'rejected' && <div className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-800"><strong>Retour admin :</strong> {kyc.review_notes}{kyc.reviewed_at ? ` • ${formatDateTime(kyc.reviewed_at)}` : ''}</div>}
              {kyc.rejection_reason && workflowState === 'rejected' && <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-800"><strong>Motif de rejet :</strong> {kyc.rejection_reason}</div>}
            </div>
          )}

          {workflowState === 'rejected' && !resubmissionMode && (
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={() => setResubmissionMode(true)}>Corriger et resoumettre le dossier</Button>
              <p className="text-sm text-slate-500">Les informations actuelles seront pré-remplies. Remplacez les pièces nécessaires avant de renvoyer le dossier.</p>
            </div>
          )}

          {shouldShowForm && (
            <form onSubmit={handleSubmitKyc} className="space-y-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Informations entreprise / boutique</h3>
                <p className="text-sm text-slate-500">Renseignez les informations légales et opérationnelles de votre activité.</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Nom légal de l’entreprise ou de la boutique</Label>
                  <Input value={kycForm.business_name} onChange={(event) => handleKycInputChange('business_name', event.target.value)} placeholder="Ex : Boutique Noki" />
                </div>
                <div>
                  <Label>Nature d’activité</Label>
                  <Input value={kycForm.business_activity} onChange={(event) => handleKycInputChange('business_activity', event.target.value)} placeholder="Ex : prêt-à-porter, high-tech..." />
                </div>
                <div>
                  <Label>Type de document entreprise</Label>
                  <Input value={kycForm.document_type} onChange={(event) => handleKycInputChange('document_type', event.target.value)} placeholder="RCCM, registre de commerce..." />
                </div>
                <div>
                  <Label>Numéro RCCM / équivalent</Label>
                  <Input value={kycForm.registration_number} onChange={(event) => handleKycInputChange('registration_number', event.target.value)} placeholder="Ex : CG-BZV-01-2024-B12" />
                </div>
                <div>
                  <Label>Identifiant fiscal / NIU</Label>
                  <Input value={kycForm.tax_identifier} onChange={(event) => handleKycInputChange('tax_identifier', event.target.value)} placeholder="Optionnel" />
                </div>
                <div>
                  <Label>Téléphone professionnel</Label>
                  <Input value={kycForm.business_contact_phone} onChange={(event) => handleKycInputChange('business_contact_phone', event.target.value)} placeholder="+242 ..." />
                </div>
                <div className="md:col-span-2">
                  <Label>Adresse professionnelle</Label>
                  <Input value={kycForm.professional_address} onChange={(event) => handleKycInputChange('professional_address', event.target.value)} placeholder="Adresse de votre activité" />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input value={kycForm.business_city} onChange={(event) => handleKycInputChange('business_city', event.target.value)} placeholder="Brazzaville" />
                </div>
                <div>
                  <Label>Pays</Label>
                  <Input value={kycForm.business_country} onChange={(event) => handleKycInputChange('business_country', event.target.value)} placeholder="Congo" />
                </div>
                <div className="md:col-span-2">
                  <Label>Email professionnel</Label>
                  <Input type="email" value={kycForm.business_contact_email} onChange={(event) => handleKycInputChange('business_contact_email', event.target.value)} placeholder="Optionnel" />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">Pièces justificatives obligatoires</h4>
                  <p className="text-sm text-slate-500">Les trois documents ci-dessous sont requis pour un dossier KYC complet.</p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  {KYC_DOCUMENT_FIELDS.map((documentField) => {
                    const currentDocument = kycForm.documents[documentField.category] || {};
                    const isUploading = uploadingCategory === documentField.category;
                    return (
                      <div key={documentField.category} className="rounded-lg border border-slate-200 bg-white p-4">
                        <div className="flex items-start gap-2">
                          <UploadCloud className="mt-0.5 h-4 w-4 text-cyan-600" />
                          <div>
                            <p className="font-medium text-slate-900">{documentField.label}</p>
                            <p className="text-xs text-slate-500">{documentField.helper}</p>
                          </div>
                        </div>
                        <Input className="mt-3" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(event) => handleDocumentUpload(documentField.category, event)} disabled={submitting || Boolean(uploadingCategory)} />
                        {currentDocument.original_filename ? (
                          <div className="mt-3 rounded-md bg-emerald-50 p-2 text-xs text-emerald-700">
                            {isUploading ? 'Téléversement...' : `Prêt : ${currentDocument.original_filename}`}
                            {currentDocument.access_url && (
                              <div>
                                <a href={currentDocument.access_url} target="_blank" rel="noreferrer" className="underline">Voir le document actuel</a>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-slate-500">Aucun fichier chargé pour le moment.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Notes vendeur</Label>
                <Textarea value={kycForm.notes} onChange={(event) => handleKycInputChange('notes', event.target.value)} placeholder="Contexte utile pour l’administration" rows={4} />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" disabled={submitting || Boolean(uploadingCategory)}>
                  {submitting ? 'Envoi du dossier...' : resubmissionMode ? 'Resoumettre le dossier KYC' : 'Soumettre le dossier KYC'}
                </Button>
                {resubmissionMode && <Button type="button" variant="outline" onClick={() => setResubmissionMode(false)} disabled={submitting}>Annuler la correction</Button>}
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Badges vendeur</CardTitle>
          <CardDescription>Les badges reflètent la vérification, l’abonnement Pro et la performance.</CardDescription>
        </CardHeader>
        <CardContent>
          {badges.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun badge pour le moment.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {badges.map((badge) => (
                <Badge key={`${badge.badge_type}-${badge.source}`} className={badge.badge_type === 'verified_seller' ? 'bg-green-100 text-green-700' : 'bg-cyan-100 text-cyan-700'}>
                  {formatBadgeLabel(badge.badge_type)}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Avis clients
          </CardTitle>
          <CardDescription>Collectez des avis après livraison avec votre lien personnel et suivez votre réputation.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-cyan-800">
                  <Link2 className="h-4 w-4" />
                  <p className="font-semibold">Lien personnel de collecte</p>
                </div>
                <p className="text-sm text-cyan-900">Envoyez ce lien à vos clients après une livraison terminée. Ils devront renseigner leur référence de livraison pour laisser un avis.</p>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleCopyReviewLink} disabled={!reviewLinkUrl}><Copy className="mr-2 h-4 w-4" />Copier</Button>
                <Button type="button" variant="outline" onClick={handleShareReviewLink} disabled={!reviewLinkUrl}><Share2 className="mr-2 h-4 w-4" />Partager</Button>
              </div>
            </div>
            <div className="mt-3 break-all rounded-lg border border-cyan-100 bg-white p-3 text-sm text-slate-700">{reviewLinkUrl || 'Lien indisponible pour le moment.'}</div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[260px,1fr]">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-medium text-slate-500">Note moyenne</p>
              <p className="mt-2 text-4xl font-semibold text-slate-900">{Number(reviewsSummary.average_rating || 0).toFixed(2)}<span className="ml-2 text-lg text-slate-500">/ 5</span></p>
              <div className="mt-3 flex items-center gap-1 text-amber-500">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star key={`summary-star-${index}`} className={`h-4 w-4 ${(index + 1) <= Math.round(Number(reviewsSummary.average_rating || 0)) ? 'fill-current' : 'text-slate-200'}`} />
                ))}
              </div>
              <p className="mt-2 text-sm text-slate-500">{reviewsSummary.total_reviews || 0} avis client(s)</p>
              {reviewsSummary.primary_badge?.label && (
                <div className="mt-3 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                  {reviewsSummary.primary_badge.label}
                </div>
              )}
            </div>

            <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-medium text-slate-500">Répartition des notes</p>
              {['5', '4', '3', '2', '1'].map((bucket) => {
                const count = Number(reviewsSummary.rating_distribution?.[bucket] || 0);
                const total = Number(reviewsSummary.total_reviews || 0);
                const width = total > 0 ? `${Math.max(6, Math.round((count / total) * 100))}%` : '0%';
                return (
                  <div key={`bucket-${bucket}`} className="flex items-center gap-3">
                    <div className="w-12 text-sm font-medium text-slate-700">{bucket}★</div>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-amber-400" style={{ width }} />
                    </div>
                    <div className="w-10 text-right text-sm text-slate-500">{count}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {reviews.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun avis disponible pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {reviews.map((review) => (
                <div key={review.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between text-sm">
                    <strong>{review.reviewer_name || review.created_by_customer || 'Client anonyme'} · {review.rating}/5</strong>
                    <span className="text-gray-500">{formatDateTime(review.created_at)}</span>
                  </div>
                  {review.comment ? (
                    <p className="mt-2 text-sm text-gray-700">{review.comment}</p>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">Sans commentaire</p>
                  )}
                  {review.delivery_reference && <p className="mt-2 text-xs text-slate-500">Référence : {review.delivery_reference}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrustCenterPanel;
